import classNames from 'classnames';
import React from 'react';

import DropdownIcon, {
	DropdownIconStyle,
} from '~/components/patterns/icons/DropdownIcon';
import useMenuExpandedState from '~/hooks/useMenuExpandedState';
import useViewportType from '~/hooks/useViewportType';
import {
	isPhase3,
} from '~/utilities/viewportSize';



type Props = {
	accountListSection?: React.ReactNode,
	accountNavButton?: React.ReactNode,
	children: React.ReactNode,
	icon: React.ReactNode,
	isExpanded: boolean,
	isSubItemsHidden?: boolean,
	label: React.ReactNode,
	onToggle: () => void,
	phase2CompactLayout?: React.ReactNode,
};

/**
 * Expandable navigation section component
 * Used for Technical section that contains sub-items
 */
const ExpandableNavSection: React.FC<Props> = (props) => {
	const {
		accountListSection,
		accountNavButton,
		children,
		icon,
		isExpanded,
		isSubItemsHidden = false,
		label,
		onToggle,
		phase2CompactLayout,
	} = props;

	const viewportType = useViewportType();
	const [
		isMenuExpanded,
		,
		isMenuMinimized,
	] = useMenuExpandedState();

	// Check if we're in Phase 3 (either naturally or through menu expansion)
	const effectivePhase3 = isPhase3(viewportType, isMenuExpanded, isMenuMinimized);

	// Show label when in Phase 3 (either naturally or through menu expansion)
	const shouldShowLabel = effectivePhase3;

	const handleToggle = React.useCallback(
		(e: React.MouseEvent) => {
			e.preventDefault();
			onToggle();
		},
		[
			onToggle,
		],
	);

	const iconClasses = classNames({
		'main-nav__expandable-icon': true,
		'main-nav__expandable-icon--expanded': isExpanded,
	});

	// Generate unique ID for ARIA attributes
	const sectionId = React.useMemo(() => {
		// Use label text to create a stable ID
		const labelText = typeof label === 'string' ? label : 'section';
		return `nav-section-${labelText.toLowerCase().replace(/\s+/g, '-')}`;
	}, [label]);

	return (
		<>
			<li className="main-nav__item main-nav__item--expandable">
				<button
					aria-controls={sectionId}
					aria-expanded={isExpanded}
					aria-label={`Toggle ${typeof label === 'string' ? label : 'navigation'} section`}
					className="main-nav__expandable-trigger"
					onClick={handleToggle}
					type="button"
				>
					{/* Render structure manually - similar to Website component pattern */}
					<span className="menu-item-layout">
						<span className="menu-item-layout__icon">
							{icon}
						</span>
						{shouldShowLabel && (
							<span className="menu-item-layout__label">
								{label}
								{/* Render DropdownIcon inside label in Phase 3, just like Website component's additionalIcon */}
								<span className={iconClasses}>
									<DropdownIcon
										isActive={isExpanded}
										size={10}
										style={DropdownIconStyle.White}
									/>
								</span>
							</span>
						)}
					</span>
				</button>
				{/* Account Navigation Button - rendered right below Technical header, outside sub-items container */}
				{accountNavButton && isExpanded && (
					<div className="main-nav__account-nav-button-wrapper">
						{accountNavButton}
					</div>
				)}
				{/* Account List Section - rendered below AccountNavButton when accounts are shown */}
				{accountListSection && isExpanded && accountListSection}
				{/* Sub-items - shown in Phase 3 or when compact layout is not active */}
				{/* Hide Sub-items when: collapsed, hidden by AccountNavButton, OR when compact layout is shown */}
				<ul
					aria-hidden={!isExpanded || isSubItemsHidden || !!phase2CompactLayout}
					className={classNames(
						'main-nav__sub-items',
						{
							'main-nav__sub-items--hidden': isSubItemsHidden || !!phase2CompactLayout,
						},
					)}
					id={sectionId}
				>
					{children}
				</ul>
			</li>
			{/* Phase 2 Compact Layout - shown instead of Sub-items when in Phase 2 */}
			{/* Only show when: expanded, compact layout provided, and Sub-items are NOT hidden (AccountListSection not visible) */}
			{/* Rendered as siblings of the expandable item, not children (to maintain valid HTML structure) */}
			{phase2CompactLayout && isExpanded && !isSubItemsHidden && phase2CompactLayout}
		</>
	);
};



export default ExpandableNavSection;
